import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import makeStyles from "utils/makeStyles";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";

const useStyles = makeStyles(() => ({
  pageWrap: {
    display: "flex",
    flexDirection: "column",
    padding: "22px 28px",
    gap: 18,
    height: "calc(100vh - 150px)",
    overflow: "hidden",
    minHeight: 0,
    boxSizing: "border-box",
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  pageTitle: { fontSize: 18, fontWeight: 500, color: "#202124" },
  pageSubtitle: { fontSize: 13, color: "#5f6368", marginTop: 4 },
  saveArea: { display: "flex", alignItems: "center", gap: 6 },
  unsavedBadge: {
    fontSize: 12,
    color: "#e37400",
    display: "flex",
    alignItems: "center",
    gap: 4,
    opacity: 0,
    transition: "opacity 0.2s",
    whiteSpace: "nowrap",
    "& svg": { fontSize: 14 },
  },
  unsavedBadgeVisible: { opacity: 1 },
  btnSave: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 22px",
    background: "#1a73e8",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 13.5,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    "&:disabled": { background: "#dadce0", color: "#9aa0a6", cursor: "not-allowed" },
    "&:not(:disabled):hover": { background: "#1557b0" },
    "& svg": { fontSize: 16 },
  },
  btnSaveSaved: { background: "#188038 !important" },
  twoCol: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
  },
  card: {
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
  },
  cardHeader: {
    padding: "16px 22px 13px",
    borderBottom: "1px solid #f1f3f4",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  cardTitle: { fontSize: 14, fontWeight: 500, color: "#202124" },
  cardSubtitle: { fontSize: 12, color: "#5f6368", marginTop: 2 },
  cardBadge: {
    fontSize: 11.5,
    fontWeight: 500,
    background: "#e8f0fe",
    color: "#1a73e8",
    padding: "3px 10px",
    borderRadius: 12,
    whiteSpace: "nowrap",
  },
  cardFooter: {
    padding: "9px 22px",
    background: "#fafafa",
    borderTop: "1px solid #f1f3f4",
    flexShrink: 0,
    fontSize: 12,
    color: "#5f6368",
    display: "flex",
    alignItems: "center",
    gap: 6,
    "& svg": { fontSize: 14, color: "#9aa0a6" },
  },
  cardBody: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    padding: "8px 0",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#dadce0", borderRadius: 4 },
  },
  alertRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "10px 22px",
    cursor: "pointer",
    transition: "background 0.12s",
    userSelect: "none",
    "&:hover": { background: "#f8f9fa" },
  },
  alertRowChecked: { background: "#f0f4ff" },
  customCb: {
    width: 18,
    height: 18,
    flexShrink: 0,
    border: "2px solid #dadce0",
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    transition: "all 0.15s",
    "& svg": { fontSize: 13, color: "#fff", opacity: 0 },
  },
  customCbChecked: {
    background: "#1a73e8",
    borderColor: "#1a73e8",
    "& svg": { opacity: 1 },
  },
  alertLabel: { flex: 1, fontSize: 13.5, color: "#3c4043", transition: "color 0.15s" },
  alertLabelChecked: { color: "#202124", fontWeight: 500 },
  alertStatus: {
    fontSize: 11.5,
    fontWeight: 500,
    padding: "2px 9px",
    borderRadius: 10,
  },
  alertStatusOn: { background: "#e6f4ea", color: "#188038" },
  alertStatusOff: { background: "#f1f3f4", color: "#9aa0a6" },
  vmCardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  scopeSection: { padding: "14px 22px 12px", flexShrink: 0 },
  scopeRow: { display: "flex", flexDirection: "column", gap: 8 },
  scopeOpt: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    border: "1.5px solid #dadce0",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s",
    background: "#fff",
    "&:hover": { borderColor: "#aecbfa" },
  },
  scopeOptSelected: { borderColor: "#1a73e8", background: "#e8f0fe" },
  radioDot: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2px solid #dadce0",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "border-color 0.15s",
  },
  radioDotSelected: { borderColor: "#1a73e8" },
  radioDotInner: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#1a73e8",
    opacity: 0,
    transition: "opacity 0.15s",
  },
  radioDotInnerSelected: { opacity: 1 },
  scopeOptLabel: { fontSize: 13.5, fontWeight: 500, color: "#202124" },
  scopeOptDesc: { fontSize: 12, color: "#5f6368" },
  allNote: {
    margin: "0 22px 14px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 6,
    background: "#e6f4ea",
    fontSize: 12.5,
    color: "#188038",
    flexShrink: 0,
    "& svg": { fontSize: 16, flexShrink: 0 },
  },
  vmPicker: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  vmSearchWrap: {
    margin: "0 22px 10px",
    position: "relative",
    flexShrink: 0,
  },
  vmSearchIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "17px !important",
    color: "#9aa0a6",
    pointerEvents: "none",
  },
  vmSearchInput: {
    width: "100%",
    padding: "9px 10px 9px 34px",
    border: "1.5px solid #dadce0",
    borderRadius: 6,
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.15s",
    boxSizing: "border-box",
    "&:focus": { borderColor: "#1a73e8" },
  },
  vmList: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    padding: "0 0 8px",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#dadce0", borderRadius: 4 },
  },
  vmRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "9px 22px",
    cursor: "pointer",
    transition: "background 0.12s",
    userSelect: "none",
    "&:hover": { background: "#f8f9fa" },
  },
  vmRowChecked: { background: "#f0f4ff" },
  vmRowLabel: { flex: 1, fontSize: 13.5, color: "#3c4043" },
  vmRowLabelChecked: { color: "#202124", fontWeight: 500 },
  vmSerial: { fontSize: 11.5, color: "#9aa0a6" },
  vmEmptyMsg: { padding: "20px 22px", fontSize: 13, color: "#9aa0a6", textAlign: "center" },
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 300,
  },
}));

const setsEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

const PushNotificationSettings = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state) => state.userReducer?.current_user);

  const [alertChoices, setAlertChoices] = useState([]);
  const [vmList, setVmList] = useState([]);
  const [enabledAlerts, setEnabledAlerts] = useState(new Set());
  const [deviceScope, setDeviceScope] = useState("all");
  const [selectedVMIds, setSelectedVMIds] = useState(new Set());
  const [vmSearch, setVmSearch] = useState("");
  const [loader, setLoader] = useState(true);
  const [saveLoader, setSaveLoader] = useState(false);
  const [savedAnim, setSavedAnim] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState(null);
  const [savedAlerts, setSavedAlerts] = useState(new Set());
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const savedRef = useRef({ alerts: new Set(), scope: "all", vmIds: new Set() });

  const isDirty = () =>
    !setsEqual(enabledAlerts, savedRef.current.alerts) ||
    deviceScope !== savedRef.current.scope ||
    !setsEqual(selectedVMIds, savedRef.current.vmIds);

  useEffect(() => {
    Promise.all([fetchAlertChoices(), fetchVMs(), fetchSettings()]).finally(() =>
      setLoader(false)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAlertChoices = async () => {
    try {
      const { data } = await window.axiosIns(
        "/push_notification_settings/alert_type_choices"
      );
      const choices = data?.push_notification_alert_choices || {};
      setAlertChoices(
        Object.entries(choices).map(([label, value]) => ({ label, value }))
      );
    } catch {
      enqueueSnackbar("Could not load alert types.");
    }
  };

  const fetchVMs = async () => {
    try {
      const { data } = await window.axiosIns("device", {
        params: { all: true, ordering: "vm_name", approval_status: "Approved" },
      });
      setVmList(
        (data?.data?.results || []).map(({ id, serial_number, vm_name }) => ({
          id,
          name: vm_name || serial_number,
          serial: serial_number,
        }))
      );
    } catch {
      // non-critical
    }
  };

  const fetchSettings = async () => {
    try {
      const params = {};
      const { data } = await window.axiosIns("push_notification_settings", { params });
      const record = (data?.results || [])[0];
      if (record) {
        const alerts = new Set(record.alert_type || []);
        const scope = (record.devices || []).length > 0 ? "specific" : "all";
        const vmIds = new Set(record.devices || []);
        setExistingRecordId(record.id);
        setEnabledAlerts(alerts);
        setDeviceScope(scope);
        setSelectedVMIds(vmIds);
        setSavedAlerts(new Set(alerts));
        if (record.updated_at) setLastUpdatedAt(new Date(record.updated_at));
        savedRef.current = { alerts: new Set(alerts), scope, vmIds: new Set(vmIds) };
      }
    } catch {
      enqueueSnackbar("Could not load push notification settings.");
    }
  };

  const handleSave = async () => {
    if (deviceScope === "specific" && selectedVMIds.size === 0) {
      enqueueSnackbar("Please select at least one vending machine.");
      return;
    }
    setSaveLoader(true);
    try {
      const body = {
        alert_type: [...enabledAlerts],
        devices: deviceScope === "all" ? [] : [...selectedVMIds],
        operator: user?.company?.company_id,
      };
      if (existingRecordId) {
        await window.axiosIns.patch(`/push_notification_settings/${existingRecordId}`, body);
      } else {
        const { data } = await window.axiosIns.post("/push_notification_settings", body);
        setExistingRecordId(data?.id);
      }
      savedRef.current = {
        alerts: new Set(enabledAlerts),
        scope: deviceScope,
        vmIds: new Set(selectedVMIds),
      };
      setSavedAlerts(new Set(enabledAlerts));
      setLastUpdatedAt(new Date());
      setSavedAnim(true);
      setTimeout(() => setSavedAnim(false), 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      enqueueSnackbar(detail || "Could not save settings. Please try again.");
    } finally {
      setSaveLoader(false);
    }
  };

  const toggleAlert = (key) => {
    setEnabledAlerts((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleVM = (id) => {
    setSelectedVMIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filteredVMs = vmList.filter(
    (v) =>
      v.name.toLowerCase().includes(vmSearch.toLowerCase()) ||
      v.serial.toLowerCase().includes(vmSearch.toLowerCase())
  );

  const sortedAlertChoices = [...alertChoices].sort(
    (a, b) => (savedAlerts.has(b.value) ? 1 : 0) - (savedAlerts.has(a.value) ? 1 : 0)
  );

  const alertBadge = `${enabledAlerts.size} / ${alertChoices.length} enabled`;
  const vmBadge =
    deviceScope === "all"
      ? "All machines"
      : selectedVMIds.size === 0
      ? "None selected"
      : `${selectedVMIds.size} selected`;
  const vmFooter =
    deviceScope === "all"
      ? `All ${vmList.length} machines are currently included.`
      : selectedVMIds.size === 0
      ? "No machines selected — select at least one."
      : `${selectedVMIds.size} of ${vmList.length} machine${selectedVMIds.size !== 1 ? "s" : ""} selected.`;

  const dirty = isDirty();

  if (loader) {
    return (
      <div className={classes.loaderWrap}>
        <CircularProgress size={32} />
      </div>
    );
  }

  return (
    <div className={classes.pageWrap}>
      {/* Header */}
      <div className={classes.pageHeader}>
        <div>
          <div className={classes.pageTitle}>Push Notification Settings</div>
          <div className={classes.pageSubtitle}>
            Choose which alerts you want to receive and on which vending machines.
          </div>
        </div>
        <div className={classes.saveArea}>
          {dirty ? (
            <div className={classes.unsavedBadge} style={{ opacity: 1 }}>
              <EditIcon />
              Unsaved changes
            </div>
          ) : lastUpdatedAt ? (
            <span style={{ fontSize: 13.5, color: "#9aa0a6", whiteSpace: "nowrap", fontStyle: "italic" }}>
              Last saved {lastUpdatedAt.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
          ) : null}
          <button
            className={`${classes.btnSave} ${savedAnim ? classes.btnSaveSaved : ""}`}
            disabled={!dirty || saveLoader}
            onClick={handleSave}
          >
            {saveLoader ? (
              <CircularProgress size={14} color="inherit" />
            ) : savedAnim ? (
              <CheckIcon />
            ) : (
              <SaveIcon />
            )}
            <span>{savedAnim ? "Saved!" : "Save Settings"}</span>
          </button>
        </div>
      </div>

      {/* Two-column cards */}
      <div className={classes.twoCol}>

        {/* LEFT: Alert Types */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>Alert Types</div>
              <div className={classes.cardSubtitle}>
                Select the alerts you want to be notified about.
              </div>
            </div>
            <div className={classes.cardBadge}>{alertBadge}</div>
          </div>

          <div className={classes.cardBody}>
            {sortedAlertChoices.map((a) => {
              const on = enabledAlerts.has(a.value);
              return (
                <div
                  key={a.value}
                  className={`${classes.alertRow} ${on ? classes.alertRowChecked : ""}`}
                  onClick={() => toggleAlert(a.value)}
                >
                  <div className={`${classes.customCb} ${on ? classes.customCbChecked : ""}`}>
                    <CheckIcon />
                  </div>
                  <div
                    className={`${classes.alertLabel} ${on ? classes.alertLabelChecked : ""}`}
                  >
                    {a.label}
                  </div>
                  <div
                    className={`${classes.alertStatus} ${
                      on ? classes.alertStatusOn : classes.alertStatusOff
                    }`}
                  >
                    {on ? "On" : "Off"}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={classes.cardFooter}>
            <InfoOutlinedIcon />
            Enabled alerts will trigger a push notification on your device.
          </div>
        </div>

        {/* RIGHT: Vending Machines */}
        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>Vending Machines</div>
              <div className={classes.cardSubtitle}>
                Apply notifications to all machines or specific ones.
              </div>
            </div>
            <div
              className={classes.cardBadge}
              style={
                deviceScope === "specific" && selectedVMIds.size === 0
                  ? { background: "#fce8e6", color: "#d93025" }
                  : undefined
              }
            >
              {vmBadge}
            </div>
          </div>

          <div className={classes.vmCardBody}>
            {/* Scope selector */}
            <div className={classes.scopeSection}>
              <div className={classes.scopeRow}>
                {[
                  {
                    key: "all",
                    label: "All Vending Machines",
                    desc: "Notifications apply to your entire fleet",
                  },
                  {
                    key: "specific",
                    label: "Specific Vending Machines",
                    desc: "Choose individual machines from your fleet",
                  },
                ].map((opt) => {
                  const sel = deviceScope === opt.key;
                  return (
                    <div
                      key={opt.key}
                      className={`${classes.scopeOpt} ${sel ? classes.scopeOptSelected : ""}`}
                      onClick={() => setDeviceScope(opt.key)}
                    >
                      <div
                        className={`${classes.radioDot} ${sel ? classes.radioDotSelected : ""}`}
                      >
                        <div
                          className={classes.radioDotInner}
                          style={{ opacity: sel ? 1 : 0 }}
                        />
                      </div>
                      <div>
                        <div className={classes.scopeOptLabel}>{opt.label}</div>
                        <div className={classes.scopeOptDesc}>{opt.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* All-machines confirmation */}
            {deviceScope === "all" && (
              <div className={classes.allNote}>
                <CheckCircleOutlineIcon />
                Notifications are active on all{" "}
                <strong style={{ margin: "0 3px" }}>{vmList.length}</strong> vending
                machines in your fleet.
              </div>
            )}

            {/* Specific VM picker */}
            {deviceScope === "specific" && (
              <div className={classes.vmPicker}>
                <div className={classes.vmSearchWrap}>
                  <SearchIcon className={classes.vmSearchIcon} />
                  <input
                    className={classes.vmSearchInput}
                    placeholder="Search vending machines…"
                    value={vmSearch}
                    onChange={(e) => setVmSearch(e.target.value)}
                  />
                </div>
                <div className={classes.vmList}>
                  {filteredVMs.length === 0 ? (
                    <div className={classes.vmEmptyMsg}>
                      No machines match your search.
                    </div>
                  ) : (
                    filteredVMs.map((vm) => {
                      const on = selectedVMIds.has(vm.id);
                      return (
                        <div
                          key={vm.id}
                          className={`${classes.vmRow} ${on ? classes.vmRowChecked : ""}`}
                          onClick={() => toggleVM(vm.id)}
                        >
                          <div
                            className={`${classes.customCb} ${
                              on ? classes.customCbChecked : ""
                            }`}
                          >
                            <CheckIcon />
                          </div>
                          <div
                            className={`${classes.vmRowLabel} ${
                              on ? classes.vmRowLabelChecked : ""
                            }`}
                          >
                            {vm.name}
                          </div>
                          <div className={classes.vmSerial}>{vm.serial}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={classes.cardFooter}>
            <InfoOutlinedIcon />
            <span>{vmFooter}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationSettings;
